import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  HeartIcon,
  DocumentTextIcon,
  PhoneIcon,
  CreditCardIcon,
  ArrowLeftIcon,
  InformationCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

export const EncounterInfoPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <button 
              onClick={() => navigate('/onecare/')}
              className="flex items-center group"
            >
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                  <HeartIcon className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                  Aimepic OneCare
                </h1>
                <p className="text-sm text-gray-800">Encounter Information</p>
              </div>
            </button>
            
            <nav className="flex space-x-8">
              <button 
                onClick={() => navigate('/onecare/patients/encounter')}
                className="flex items-center text-gray-900 hover:text-blue-600 transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Encounter Options
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4">
            Hospital Encounter
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              Information & Resources
            </span>
          </h1>
          <p className="text-xl text-gray-900 max-w-3xl mx-auto">
            Find information and resources related to your hospital encounter, 
            including discharge instructions, billing, and follow-up care.
          </p>
        </div>

        {/* Information Cards */}
        <div className="grid gap-8 mb-12">
          {/* Discharge Instructions */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                <DocumentTextIcon className="w-8 h-8 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                  Understanding Your Discharge Instructions
                </h3>
                <p className="text-gray-900 text-base leading-relaxed mb-6">
                  Your discharge instructions contain important information about your care after leaving the hospital, 
                  including medications, follow-up appointments, and activity restrictions.
                </p>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-green-800 mb-2">Important reminders:</h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• Take all medications as prescribed</li>
                    <li>• Keep all follow-up appointments</li>
                    <li>• Watch for warning signs mentioned in your instructions</li>
                    <li>• Contact your doctor if you have concerns</li>
                  </ul>
                </div>

                <p className="text-sm text-gray-900">
                  If you cannot locate your discharge instructions or need clarification, 
                  please contact your care team or the nursing station where you were treated.
                </p>
              </div>
            </div>
          </div>

          {/* Medical Records */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg">
                <InformationCircleIcon className="w-8 h-8 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                  Accessing Your Medical Records
                </h3>
                <p className="text-gray-900 text-base leading-relaxed mb-6">
                  You have the right to access your medical records. Our Health Information Management 
                  department can help you request copies of your medical records, test results, and imaging studies.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">To request records:</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Call Health Information: (555) 123-RECORDS</li>
                      <li>• Visit the HIM office in person</li>
                      <li>• Submit a written request</li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">What you'll need:</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Photo identification</li>
                      <li>• Date of service</li>
                      <li>• Specific records requested</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Billing Information */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
                <CreditCardIcon className="w-8 h-8 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                  Billing & Insurance Information
                </h3>
                <p className="text-gray-900 text-base leading-relaxed mb-6">
                  Understanding your hospital bill and insurance coverage can be complex. 
                  Our Patient Financial Services team is here to help explain charges and payment options.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">For billing questions:</h4>
                    <div className="flex items-center space-x-3 mb-2">
                      <PhoneIcon className="h-4 w-4 text-purple-600" />
                      <span className="text-sm text-gray-700">(555) 123-BILLS</span>
                    </div>
                    <p className="text-xs text-gray-900">Monday-Friday, 8AM-5PM</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Payment assistance:</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• Financial assistance programs available</li>
                      <li>• Payment plan options</li>
                      <li>• Insurance claim support</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Follow-up Care */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center shadow-lg">
                <HeartIcon className="w-8 h-8 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                  Follow-up Care Resources
                </h3>
                <p className="text-gray-900 text-base leading-relaxed mb-6">
                  Proper follow-up care is essential for your recovery. Here are resources to help you 
                  continue your care after your hospital encounter.
                </p>
                
                <div className="space-y-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h4 className="font-semibold text-orange-800 mb-2">Scheduling appointments:</h4>
                    <p className="text-sm text-orange-700 mb-2">
                      Call our scheduling line at <span className="font-semibold">(555) 123-APPT</span> to schedule 
                      follow-up appointments with your doctor or specialists.
                    </p>
                  </div>
                  
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h4 className="font-semibold text-orange-800 mb-2">Home health services:</h4>
                    <p className="text-sm text-orange-700">
                      If you need home health care, physical therapy, or other services at home, 
                      our discharge planning team can help coordinate these services.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Need to communicate section */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-8">
          <div className="flex items-start space-x-4">
            <ExclamationCircleIcon className="h-8 w-8 text-yellow-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-yellow-900 mb-3">
                Need to communicate with hospital staff?
              </h3>
              <p className="text-yellow-800 mb-4">
                If you have questions, concerns, or need to provide feedback about your hospital encounter, 
                you can connect directly with our patient communication portal.
              </p>
              <button
                onClick={() => navigate('/onecare/patients/feedback')}
                className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
              >
                Contact Hospital Staff
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-20">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm text-gray-800">
              <HeartIcon className="h-4 w-4 mr-2" />
              <span>HIPAA-Ready & Secure</span>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-gray-800">
              <button
                onClick={() => window.open('/patient/privacy', '_blank')}
                className="hover:text-blue-600 transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => window.open('/patient/help', '_blank')}
                className="hover:text-blue-600 transition-colors"
              >
                Help & Support
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EncounterInfoPage;